// Typographie — Space Grotesk (display) + Inter (corps), portée de
// mobile/src/theme/fonts.ts. En web on peut utiliser `font-weight` (pas de
// faux-gras navigateur) : chaque variante porte donc son poids explicite.
// Les familles pointent vers les variables next/font (--font-display / --font-body).

export const fontStacks = {
  display:
    "var(--font-display), 'Space Grotesk', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  body: "var(--font-body), 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  // Fraunces — la serif éditoriale déjà chargée par `layout.tsx`. Elle était utilisée en
  // familles et tailles écrites à la main dans les séparateurs d'année ; elle a désormais
  // ses variantes ici, comme les deux autres.
  serif:
    "var(--font-serif), 'Fraunces', ui-serif, Georgia, 'Times New Roman', serif",
} as const;

export type FontFamily = keyof typeof fontStacks;

export interface FontVariant {
  family: FontFamily;
  size: number; // px
  lineHeight: number; // px
  weight: number;
  letterSpacing?: number; // px
}

export const typography = {
  displayXl: { family: 'display', size: 32, lineHeight: 36, weight: 700, letterSpacing: -0.5 },
  title: { family: 'display', size: 24, lineHeight: 30, weight: 600, letterSpacing: -0.5 },
  subtitle: { family: 'display', size: 20, lineHeight: 26, weight: 600 },
  // Serif éditoriale : les séparateurs d'année de la bibliothèque, et le titre d'une carte
  // depuis qu'il a quitté la photographie pour le bandeau blanc. Le titre n'est plus tenu
  // aux 24 px que réclamait le contraste sur une image — la taille redevient un choix.
  serifSection: { family: 'serif', size: 22, lineHeight: 28, weight: 600, letterSpacing: -0.4 },
  serifTitle: { family: 'serif', size: 21, lineHeight: 26, weight: 600, letterSpacing: -0.3 },
  bodyLg: { family: 'body', size: 17, lineHeight: 24, weight: 400 },
  body: { family: 'body', size: 15, lineHeight: 22, weight: 400 },
  bodyMedium: { family: 'body', size: 15, lineHeight: 22, weight: 500 },
  bodySm: { family: 'body', size: 13, lineHeight: 18, weight: 400 },
  bodySmMedium: { family: 'body', size: 13, lineHeight: 18, weight: 500 },
  bodySmSemibold: { family: 'body', size: 13, lineHeight: 18, weight: 600 },
  caption: { family: 'body', size: 11, lineHeight: 14, weight: 500 },
  button: { family: 'body', size: 15, lineHeight: 20, weight: 600 },
} satisfies Record<string, FontVariant>;

export type TypographyToken = keyof typeof typography;

/**
 * ⭐⭐ LES DEUX GRAISSES D'UN NOM DE PERSONNE — fixes, identiques sur TOUS les écrans.
 *
 * Cf. `CLAUDE.md` §A.9 ter : prénom en graisse légère, nom de famille en graisse renforcée. ⭐ Seule
 * la TAILLE dépend de l'écran ; la graisse, jamais — c'est de la mise en forme.
 *
 * ── ⛔ CE QU'IL Y AVAIT LÀ AVANT, ET POURQUOI C'ÉTAIT FAUX ──────────────────────────────────
 *
 * Une table renforçait le poids **relativement au style ambiant** de l'écran. Même règle, résultats
 * opposés : l'écart valait 200 là où l'ambiance est légère (`body`, `bodySm`), 100 sur un titre ou
 * une pastille, et **ZÉRO** sur `button` et `bodySmSemibold`, où le prénom et le nom sortaient à
 * l'identique.
 *
 * ── ⚠️ LA FAMILLE EST FIXE ELLE AUSSI, ET CE N'EST PAS UN CHOIX ─────────────────────────────
 *
 * Space Grotesk n'est self-hostée qu'en 600 et 700 : elle n'a AUCUNE graisse légère. Demander un
 * 400 dedans ne rendrait pas un nom léger — le navigateur prendrait le 600 le plus proche, et les
 * deux parties du nom sortiraient identiques. Un nom rendu dans un jeton de display (le titre de
 * profil) passe donc en Inter. Il paraîtra moins présent : ⛔ à rattraper par la taille ou la
 * couleur, jamais en remontant la graisse.
 *
 * ⭐ Les deux valeurs sont celles de la feuille des amis en commun du mobile — le rendu jugé juste
 * à l'écran, et la seule référence admise.
 */
export const GRAISSE_DU_NOM = {
  famille: fontStacks.body,
  legere: 400,
  renforcee: 600,
} as const;

// Convertit une variante en style CSS applicable (React CSSProperties).
export function fontStyle(token: TypographyToken): {
  fontFamily: string;
  fontSize: string;
  lineHeight: string;
  fontWeight: number;
  letterSpacing?: string;
} {
  const v: FontVariant = typography[token];
  return {
    fontFamily: fontStacks[v.family],
    fontSize: `${v.size}px`,
    lineHeight: `${v.lineHeight}px`,
    fontWeight: v.weight,
    ...(v.letterSpacing !== undefined ? { letterSpacing: `${v.letterSpacing}px` } : {}),
  };
}
